/**
 * The subject, level and exam board vocabulary, shared by the browser and the
 * server.
 *
 * These three values are the axis the whole Marketplace browses on – the chip
 * row, the filter dropdowns, the facet counts, and a URL per subject. They were
 * free text typed into the publish dialog, which drifts: "Biology", "biology"
 * and "Biology " are three chips, three counts and three subject pages holding
 * a third of the listings each, and it gets worse the more people publish.
 *
 * The fix is to pick from a list rather than to repair the typing afterwards,
 * so the publish dialog now offers the same grouped subject picker the
 * presentation composer has always used. That list lives here rather than in
 * app.js because the server has to agree with it: the publish endpoint is a
 * public API and a client that posts whatever it likes must not be able to
 * reintroduce the drift the dialog no longer allows.
 *
 * Loaded as a plain script in the browser and required in Node, the same way
 * markSchemeFilter.js is.
 */
(function (root) {
    'use strict';

    // Grouped, because sixty subjects as one flat list is not a thing anyone
    // can read. The groups are presentation only – nothing is stored against
    // them and a subject can be moved between them freely.
    const SUBJECT_GROUPS = [
        { group: 'Sciences', subjects: ['Biology', 'Chemistry', 'Physics', 'Combined Science', 'Environmental Science', 'Geology', 'Sports Science & PE'] },
        { group: 'Mathematics', subjects: ['Mathematics', 'Further Mathematics', 'Statistics'] },
        { group: 'Social Sciences', subjects: ['Economics', 'Business Studies', 'Accounting', 'Psychology', 'Sociology', 'Politics', 'Law', 'Criminology'] },
        { group: 'Humanities', subjects: ['History', 'Geography', 'Religious Studies', 'Philosophy', 'Classical Civilisation', 'Archaeology'] },
        { group: 'English', subjects: ['English Language', 'English Literature', 'English Language & Literature'] },
        { group: 'Modern Languages', subjects: ['French', 'Spanish', 'German', 'Italian', 'Chinese (Mandarin)', 'Portuguese', 'Russian', 'Japanese', 'Arabic', 'Polish', 'Urdu'] },
        { group: 'Classical Languages', subjects: ['Latin', 'Ancient Greek'] },
        { group: 'Arts & Media', subjects: ['Art & Design', 'Drama & Theatre Studies', 'Film Studies', 'Media Studies', 'Music', 'Music Technology', 'Art History', 'Photography', 'Graphic Design'] },
        { group: 'Technology', subjects: ['Computer Science', 'Design Technology', 'Food Technology', 'Electronics', 'ICT'] },
        { group: 'Health & Social', subjects: ['Health & Social Care', 'Childcare', 'Nursing Studies', 'Applied Science'] },
        // Primary only carries what primary alone teaches. Maths, history and
        // geography are the same subjects at Year 3 as at GCSE — the level is
        // what changes, not the subject — and a subject may appear in only one
        // group, so they stay where they are and the searchable picker finds
        // them from either direction.
        //
        // The names here are deliberately not 'English', 'Science' or
        // 'Computing': utils/facets.js adds real subject names to its lookup
        // AFTER its aliases, so a real name shadows an alias of the same key.
        // Adding those three would silently re-route every historic listing
        // that typed "english" away from English Language, "science" away from
        // Combined Science and "computing" away from Computer Science,
        // splitting marketplace chips that have counts against them today.
        //
        // The last six are Scotland's Curriculum for Excellence areas, which do
        // not split the way England's subjects do (social studies is history
        // and geography together at primary). RME and Roman Catholic RE are two
        // different syllabuses for two kinds of school, not one subject.
        { group: 'Primary', subjects: ['Primary English', 'Primary Science', 'Phonics', 'PSHE & Citizenship', 'Topic',
            'Social Studies', 'Technologies', 'Expressive Arts', 'Health & Wellbeing',
            'Religious & Moral Education', 'Roman Catholic Religious Education'] },
        // The 11+ reasoning papers. English, maths and science at 11+ are
        // filed under Primary English, Mathematics and Primary Science, the
        // subjects they are; these two exist only as entrance-exam subjects.
        { group: 'Entrance exams', subjects: ['Verbal Reasoning', 'Non-Verbal Reasoning'] },
    ];

    const SUBJECTS = SUBJECT_GROUPS.reduce((all, g) => all.concat(g.subjects), []);

    // Wider than the mark scheme picker's GCSE/A-Level pair: that list describes
    // the specs we hold, this one describes who a lesson is for, and plenty of
    // what gets shared is KS3 or primary.
    const LEVELS = ['Primary', '11+', 'KS3', 'GCSE', 'IGCSE', 'A-Level', 'BTEC', 'IB'];

    // The National Curriculum is not an awarding body but it is the
    // specification primary and KS3 are taught to, so it sits on this list
    // beside them — utils/facets.js canonicalises against it and setFacetSelect
    // renders it as a normal option rather than "(not on the list)".
    const BOARDS = ['AQA', 'Edexcel', 'OCR', 'WJEC', 'Eduqas', 'CIE', 'SQA', 'IB', 'National Curriculum', 'Curriculum for Excellence',
        'ISEB', 'ISEB Pre-Tests', 'SEAG'];

    // Note LEVELS above does NOT list Year 1–6 even though the composer's level
    // pickers now offer them. The asymmetry is deliberate: the composer's level
    // is a pitch dial — fourteen values telling the model how hard to write —
    // while this facet level is a browse axis, seven buckets a marketplace chip
    // can carry a count against. Year 1–6 fold into Primary here; see the year
    // aliases in utils/facets.js.

    // Tutoring asks about a few things no exam board sets and the Marketplace
    // does not browse by, so they are added here for tutoring's pickers only
    // rather than to SUBJECT_GROUPS, which is the Marketplace's own axis.
    const TUTORING_GROUPS = SUBJECT_GROUPS.concat([
        { group: 'Entrance exams & skills', subjects: ['11+ Preparation', 'Study Skills'] },
    ]);
    // Who a tutor teaches, in the words families use. utils/tutorMatch.js
    // levelOf() maps each of these, so an enquiry and a tutor meet on them.
    const TUTORING_LEVELS = ['Primary', '11+', 'KS3', 'GCSE', 'IGCSE', 'A-Level', 'IB', 'BTEC', 'University', 'Adult learners'];

    const api = { SUBJECT_GROUPS, SUBJECTS, LEVELS, BOARDS, TUTORING_GROUPS, TUTORING_LEVELS };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.Subjects = api;
}(typeof globalThis !== 'undefined' ? globalThis : this));
